import React from "react";

// Cookie consent banner — RODO/GDPR + Google Consent Mode v2.
// Storage: localStorage["lc-consent"] = {analytics, marketing, timestamp}.
// Reopen via window event "lc-open-consent" (footer link dispatches).

const CC_KEY = "lc-consent";

const ccPushUpdate = (analytics, marketing) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(["consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied"
  }]);
  window.dataLayer.push({
    event: "consent_update",
    consent_analytics: analytics,
    consent_marketing: marketing
  });
};

const CookieConsent = () => {
  // SSR-safe defaults (match prerender'd HTML so React doesn't flag hydration mismatch).
  // Real values are read from localStorage in useEffect after mount.
  const [show, setShow] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [prefs, setPrefs] = React.useState({ analytics: false, marketing: false });

  React.useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(CC_KEY) || "null");
      if (s) {
        setPrefs({ analytics: !!s.analytics, marketing: !!s.marketing });
      } else {
        setShow(true); // first visit, no stored choice → reveal banner
      }
    } catch {
      setShow(true);
    }
    const handler = () => { setExpanded(true); setShow(true); };
    window.addEventListener("lc-open-consent", handler);
    return () => window.removeEventListener("lc-open-consent", handler);
  }, []);

  const save = (analytics, marketing) => {
    try {
      localStorage.setItem(CC_KEY, JSON.stringify({
        analytics, marketing, timestamp: Date.now(), v: 1
      }));
    } catch {}
    ccPushUpdate(analytics, marketing);
    setPrefs({ analytics, marketing });
    setShow(false);
    setExpanded(false);
  };

  if (!show) return null;

  const acceptAll = () => save(true, true);
  const rejectAll = () => save(false, false);
  const saveCustom = () => save(prefs.analytics, prefs.marketing);

  return (
    <div className="cc-root" role="dialog" aria-label="Ustawienia plików cookies" aria-modal="false">
      <div className="cc-card">
        <div className="cc-head">
          <h3 className="cc-h">Pliki cookies</h3>
          <p className="cc-body">
            Używamy plików cookies, aby ulepszać działanie strony i analizować ruch.
            Możesz zaakceptować wszystkie, odrzucić niezbędne minimum lub wybrać kategorie.
            Szczegóły w <a className="cc-link" href="/polityka-prywatnosci">Polityce prywatności</a>.
          </p>
        </div>

        {expanded && (
          <div className="cc-cats">
            <label className="cc-cat">
              <div className="cc-cat-head">
                <span className="cc-cat-name">Niezbędne</span>
                <span className="cc-toggle cc-toggle-on cc-toggle-locked" aria-label="Niezbędne — zawsze włączone">
                  <span className="cc-toggle-thumb"/>
                </span>
              </div>
              <p className="cc-cat-desc">Wymagane do działania strony — routing, formularze, sesja. Nie można wyłączyć.</p>
            </label>
            <label className="cc-cat">
              <div className="cc-cat-head">
                <span className="cc-cat-name">Analityczne</span>
                <button
                  type="button"
                  className={"cc-toggle" + (prefs.analytics ? " cc-toggle-on" : "")}
                  onClick={() => setPrefs(p => ({...p, analytics: !p.analytics}))}
                  aria-pressed={prefs.analytics}
                  aria-label={"Analityczne — " + (prefs.analytics ? "włączone" : "wyłączone")}
                >
                  <span className="cc-toggle-thumb"/>
                </button>
              </div>
              <p className="cc-cat-desc">Google Analytics 4 — pomaga rozumieć jak korzystasz ze strony i co optymalizować.</p>
            </label>
            <label className="cc-cat">
              <div className="cc-cat-head">
                <span className="cc-cat-name">Marketingowe</span>
                <button
                  type="button"
                  className={"cc-toggle" + (prefs.marketing ? " cc-toggle-on" : "")}
                  onClick={() => setPrefs(p => ({...p, marketing: !p.marketing}))}
                  aria-pressed={prefs.marketing}
                  aria-label={"Marketingowe — " + (prefs.marketing ? "włączone" : "wyłączone")}
                >
                  <span className="cc-toggle-thumb"/>
                </button>
              </div>
              <p className="cc-cat-desc">Meta Pixel, Google Ads — pozwala mierzyć skuteczność kampanii i remarketingować.</p>
            </label>
          </div>
        )}

        <div className="cc-actions">
          {expanded ? (
            <>
              <button className="lc-btn lc-btn-ghost" onClick={rejectAll}>Odrzuć wszystkie</button>
              <button className="lc-btn lc-btn-primary" onClick={saveCustom}>Zapisz wybór <span className="lc-btn-arr">↗</span></button>
            </>
          ) : (
            <>
              <button className="lc-btn lc-btn-link" onClick={() => setExpanded(true)}>Dostosuj</button>
              <button className="lc-btn lc-btn-ghost" onClick={rejectAll}>Odrzuć wszystkie</button>
              <button className="lc-btn lc-btn-primary" onClick={acceptAll}>Akceptuj wszystkie <span className="lc-btn-arr">↗</span></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export { CookieConsent };
